import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Calendar,
  Clock,
  Building2,
  Tag,
  FileText,
  Link2,
  ArrowLeftRight,
  Banknote,
  ChevronRight
} from 'lucide-react';
import { useTransactionQuery, useDeleteTransaction, useCategoriesQuery } from '../hooks/useQueries';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fetchMediaAsBlob } from '../services/api';
import { getIconComponent } from '../components/common/IconPicker';
import ConfirmModal from '../components/common/ConfirmModal';
import AttachmentViewerModal from '../components/common/AttachmentViewerModal';

// Relative time helper
function timeAgo(dateStr, timeStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (timeStr) {
    const [h, min] = timeStr.split(':').map(Number);
    date.setHours(h, min);
  }
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  if (diffD < 30) return `${Math.floor(diffD / 7)}w ago`;
  return '';
}

export default function TransactionDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formatAmount, formatDate, formatTime, config } = useConfig();
  const { user } = useAuth();
  const { toast } = useToast();
  const isTaxUser = user?.email === import.meta.env.VITE_TAX_ALLOWED_EMAIL;

  const { data: transaction, isLoading } = useTransactionQuery(id);
  const { data: categories = [] } = useCategoriesQuery();
  const deleteTransactionMutation = useDeleteTransaction();

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [attachmentViewer, setAttachmentViewer] = useState({
    isOpen: false, src: null, mimeType: null, filename: null, loading: false
  });

  // Get category info
  const categoryInfo = useMemo(() => {
    if (!transaction) return null;
    const catName = transaction.category?.name || transaction.category;
    const category = categories.find(c => c.name === catName && c.type === transaction.type);
    if (category) {
      return { icon: getIconComponent(category.icon), color: category.color, name: category.name };
    }
    return null;
  }, [transaction, categories]);

  const handleDelete = async () => {
    try {
      await deleteTransactionMutation.mutateAsync(id);
      toast.success('Transaction deleted');
      navigate('/transactions');
    } catch {
      toast.error('Failed to delete transaction');
    }
    setDeleteConfirm(false);
  };

  const handleViewAttachment = async () => {
    if (!transaction?.attachment?.url) return;
    setAttachmentViewer(prev => ({ ...prev, loading: true }));
    try {
      const mimeType = transaction.attachment.mimeType;
      const mediaUrl = await fetchMediaAsBlob(transaction.attachment.url, mimeType);
      setAttachmentViewer({ isOpen: true, src: mediaUrl, mimeType, filename: transaction.attachment.filename || 'attachment', loading: false });
    } catch {
      toast.error('Failed to load attachment');
      setAttachmentViewer(prev => ({ ...prev, loading: false }));
    }
  };

  const closeAttachmentViewer = () => {
    if (attachmentViewer.src) URL.revokeObjectURL(attachmentViewer.src);
    setAttachmentViewer({ isOpen: false, src: null, mimeType: null, filename: null, loading: false });
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="w-9 h-9 rounded-xl bg-surface-200" />
          <div className="h-5 bg-surface-200 rounded w-24" />
          <div className="w-9 h-9" />
        </div>
        <div className="text-center py-6">
          <div className="w-10 h-10 rounded-xl bg-surface-200 mx-auto mb-3" />
          <div className="h-8 bg-surface-200 rounded w-24 mx-auto mb-2" />
          <div className="h-4 bg-surface-100 rounded w-40 mx-auto" />
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4 space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-14 bg-surface-50 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-500">Transaction not found</p>
        <button onClick={() => navigate('/transactions')} className="mt-4 text-primary-600 font-medium">
          Back to Transactions
        </button>
      </div>
    );
  }

  const isTransfer = transaction.type === 'transfer';
  const typeColor = transaction.type === 'income' ? config.incomeColor : transaction.type === 'transfer' ? 'var(--accent-bg)' : config.expenseColor;
  const relTime = timeAgo(transaction.date, transaction.time);
  const displayAmount = transaction.type === 'income' && isTaxUser
    ? transaction.amountInINR - (transaction.tdsAmount || 0)
    : transaction.amountInINR;

  const CategoryIcon = categoryInfo?.icon;

  return (
    <div className="max-w-2xl mx-auto pb-24">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl bg-surface-100 flex items-center justify-center text-surface-600 hover:bg-surface-200 transition-colors"
        >
          <ArrowLeft className="w-[18px] h-[18px]" />
        </button>
        <span className="text-[15px] font-semibold text-surface-900">Transaction</span>
        <div className="w-9 h-9" /> {/* spacer */}
      </div>

      {/* Hero: Category badge + Amount + Description */}
      <div className="text-center mb-5">
        {/* Category pill */}
        {!isTransfer && categoryInfo ? (
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-3"
            style={{ backgroundColor: `${categoryInfo.color}15` }}
          >
            {CategoryIcon && <CategoryIcon className="w-3.5 h-3.5" style={{ color: categoryInfo.color }} />}
            <span className="text-xs font-semibold" style={{ color: categoryInfo.color }}>{categoryInfo.name}</span>
          </div>
        ) : isTransfer ? (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-3 bg-primary-50">
            <ArrowLeftRight className="w-3.5 h-3.5 text-primary-600" />
            <span className="text-xs font-semibold text-primary-600">Transfer</span>
          </div>
        ) : null}

        {/* Amount */}
        <p className="text-4xl font-extrabold text-surface-900 tracking-tight">
          {formatAmount(displayAmount)}
        </p>

        {/* TDS info */}
        {isTaxUser && transaction.type === 'income' && transaction.tdsAmount > 0 && (
          <p className="text-xs text-surface-400 mt-1">
            TDS: {formatAmount(transaction.tdsAmount)} | Gross: {formatAmount(transaction.amountInINR)}
          </p>
        )}

        {/* Description + relative time */}
        <p className="text-sm text-surface-500 mt-2 px-4">
          {transaction.description || 'No description'}
        </p>
        {relTime && (
          <p className="text-xs text-surface-400 mt-1">{relTime} &middot; {formatDate(transaction.date)}</p>
        )}
      </div>

      {/* Details Card */}
      <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mx-1">
        {/* Date + Time row */}
        <div className="flex border-b border-surface-100">
          <div className="flex-1 p-3.5 flex items-center gap-3 border-r border-surface-100">
            <Calendar className="w-4 h-4 text-surface-400 flex-shrink-0" />
            <div>
              <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Date</p>
              <p className="text-sm font-semibold text-surface-900">{formatDate(transaction.date)}</p>
            </div>
          </div>
          {transaction.time && (
            <div className="flex-1 p-3.5 flex items-center gap-3">
              <Clock className="w-4 h-4 text-surface-400 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Time</p>
                <p className="text-sm font-semibold text-surface-900">{formatTime(transaction.time)}</p>
              </div>
            </div>
          )}
        </div>

        {/* Account + Type row */}
        <div className="flex border-b border-surface-100">
          <div className="flex-1 p-3.5 flex items-center gap-3 border-r border-surface-100">
            <Building2 className="w-4 h-4 text-surface-400 flex-shrink-0" />
            <div>
              <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider">
                {isTransfer ? 'From' : 'Account'}
              </p>
              <p className="text-sm font-semibold text-surface-900">{transaction.account?.name || transaction.bankName}</p>
            </div>
          </div>
          {isTransfer && transaction.toAccount ? (
            <div className="flex-1 p-3.5 flex items-center gap-3">
              <Building2 className="w-4 h-4 text-surface-400 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider">To</p>
                <p className="text-sm font-semibold text-surface-900">{transaction.toAccount?.name || transaction.toAccount}</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 p-3.5 flex items-center gap-3">
              <Tag className="w-4 h-4 text-surface-400 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Type</p>
                <p className="text-sm font-semibold capitalize" style={{ color: typeColor }}>
                  {transaction.type}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        {transaction.notes && (
          <div className="p-3.5 flex items-start gap-3 border-b border-surface-100">
            <FileText className="w-4 h-4 text-surface-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider">Notes</p>
              <p className="text-sm text-surface-700 mt-0.5 whitespace-pre-wrap">{transaction.notes}</p>
            </div>
          </div>
        )}

        {/* Attachment */}
        {transaction.attachment?.url && (
          <button
            onClick={handleViewAttachment}
            disabled={attachmentViewer.loading}
            className="w-full p-3.5 flex items-center gap-3 hover:bg-surface-50 transition-colors text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
              <Link2 className="w-4 h-4 text-primary-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-surface-900 truncate">
                {attachmentViewer.loading ? 'Loading...' : (transaction.attachment.filename || 'Attachment')}
              </p>
              <p className="text-xs text-surface-400">Tap to view</p>
            </div>
            <ChevronRight className="w-4 h-4 text-surface-300 flex-shrink-0" />
          </button>
        )}
      </div>

      {/* Bottom Action Bar — above bottom nav */}
      <div className="fixed bottom-[72px] left-0 right-0 xl:left-72 p-4 pb-2 bg-gradient-to-t from-[var(--page-bg,#f8fafc)] via-[var(--page-bg,#f8fafc)] to-transparent z-20 safe-area-bottom">
        <div className="max-w-2xl mx-auto flex gap-3">
          <button
            onClick={() => navigate(`/transactions/${id}/edit`)}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-white border-2 border-surface-200 rounded-xl font-semibold text-sm text-surface-900 hover:bg-surface-50 transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => setDeleteConfirm(true)}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-expense-50 border-2 border-expense-200 rounded-xl font-semibold text-sm text-expense-600 hover:bg-expense-100 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Transaction"
        message="Are you sure you want to delete this transaction? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      {/* Attachment Viewer */}
      <AttachmentViewerModal
        isOpen={attachmentViewer.isOpen}
        onClose={closeAttachmentViewer}
        src={attachmentViewer.src}
        mimeType={attachmentViewer.mimeType}
        filename={attachmentViewer.filename}
      />
    </div>
  );
}
